import { useEffect, useState, useRef } from "react";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Label } from "./ui/label";
import { api } from "../lib/api";

/**
 * Iter 49.1 — Lightweight currency picker for document create dialogs.
 *
 * Renders a Shadcn <Select> populated from /api/billing/currencies/list.
 * Cached at module scope so multiple instances on the same page share one
 * network round-trip. Defaults to INR — every legacy / fresh create flow
 * keeps working unchanged when the operator doesn't touch the picker.
 *
 * Used by:
 *   • Invoices.jsx — new-invoice dialog
 *   • Proposals.jsx — new-proposal dialog
 *   • PurchaseOrders.jsx — new-PO dialog
 *
 * Credit Notes intentionally do NOT use this — CN currency is inherited
 * from the source invoice automatically on the backend.
 */
let _cache = null;
let _inflight = null;

async function loadCurrencies() {
    if (_cache) return _cache;
    if (_inflight) return _inflight;
    _inflight = (async () => {
        try {
            const { data } = await api.get("/billing/currencies/list");
            _cache = data?.items || [];
            return _cache;
        } catch {
            // Defensive fallback — render INR-only when the registry is unreachable
            _cache = [{ code: "INR", symbol: "₹", name: "Indian Rupee" }];
            return _cache;
        } finally {
            _inflight = null;
        }
    })();
    return _inflight;
}

export function CurrencySelect({
    value,
    onChange,
    disabled = false,
    label = "Currency",
    testid = "currency-select",
    className = "",
}) {
    const [options, setOptions] = useState(_cache || []);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        if (!_cache) {
            loadCurrencies().then((items) => {
                if (mountedRef.current) setOptions(items);
            });
        }
        return () => { mountedRef.current = false; };
    }, []);

    const selected = options.find((o) => o.code === value) || options[0];

    return (
        <div className={className}>
            {label && <Label className="text-xs font-semibold">{label}</Label>}
            <Select value={value || "INR"} onValueChange={onChange} disabled={disabled}>
                <SelectTrigger className="mt-1.5 h-9 text-xs" data-testid={testid}>
                    <SelectValue placeholder="INR — Indian Rupee">
                        {selected ? `${selected.code} ${selected.symbol} — ${selected.name}` : "INR ₹ — Indian Rupee"}
                    </SelectValue>
                </SelectTrigger>
                <SelectContent>
                    {(options.length ? options : [{ code: "INR", symbol: "₹", name: "Indian Rupee" }]).map((c) => (
                        <SelectItem key={c.code} value={c.code} data-testid={`${testid}-opt-${c.code}`}>
                            <span className="font-mono mr-2">{c.code}</span>
                            <span className="mr-1.5">{c.symbol}</span>
                            <span className="text-slate-500">— {c.name}</span>
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
            <p className="mt-1 text-[10px] text-slate-500">
                Set at create time, immutable thereafter. Display-only — no FX conversion.
            </p>
        </div>
    );
}

export default CurrencySelect;
